// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// jevHelp — the Jev window's hover explanations (Leland, 2026-10-07: "some of
// this data is vague … no labels or shortened, so maybe the solution is tooltips
// on hover"). One place for every label's plain-English meaning; jevHelp.test.js
// fails if a column, tile, chart, card or field label has none.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export const COLUMN_HELP = {
  Time: 'When FAV asked Jev (your local time). "replay" = a past FAV call re-run through Jev by PRED, not a live call.',
  "Keller ID": "The job's Keller ID. Type it in the search box to find its calls.",
  Status:
    "Did Jev answer? ✓ ok = answered · – skipped = not asked (unknown floor type, or no labour $ on the invoice) · ⏱ timeout = no answer within 1.5 seconds (mostly the first call after the function sat idle) · ✕ error = Jev or the network failed.",
  Floor: "The job's floor type, as sent to Jev.",
  "Labour $": "The job's labour dollars from the invoice — the strongest single clue to how long a job takes.",
  Jev: "Jev's pick for how many working days the install will take: 1, 2, 3 or 4+.",
  "Jev probabilities":
    "Jev's chance for each answer (1, 2, 3, 4+ days) as one bar, coloured as in the legend under the table. Hover a segment for its percentage.",
  "P(>1 day)": "Jev's chance this job takes MORE than one working day.",
  "Table P(>1)":
    "The same chance from past jobs alone — similar finished installs (same floor type, similar labour $), no AI. The yardstick Jev should beat.",
  "FAV booked":
    'What FAV did with this call: the date it reserved (FAV books every job as 1 day today). "not reserved" = FAV found no open slot.',
  "Booking now":
    "The job's booking in the Scheduler right now: booked working days, then its status code (s4 = scheduled · s8–s15 = done · s20 = cancelled · s30–s31 = repair). ✓ finished = it can be graded.",
  Actual:
    'Once the job finishes in FileMaker: the real number of working days (Sundays not counted), ✓ if Jev\'s pick was right, ✗ if not. "pending" = not finished yet. "cancelled" = cancelled or written off — never graded.',
  ms: "How long the Jev call took, in milliseconds (looking the job up + asking Jev). FAV's booking never waits on it.",
};

// Tile labels; "(Live)" / "(Replays)" variants share their base label's help.
export const TILE_HELP = {
  Calls: "Jev calls in the list (after your filters), and the time range they cover.",
  Answered: "Calls where Jev returned an answer.",
  Timeouts:
    'Calls where Jev didn\'t answer within 1.5 seconds — mostly the first call after the function sat idle (a "cold start"). The booking is never affected.',
  "Skipped / errors": "Skipped = Jev wasn't asked (unknown floor type, or no labour $). Errors = Jev or the network failed.",
  "Median latency": "The typical time for a Jev call — half were faster. 90th pct = 9 in 10 were faster than this.",
  "Jev says > 1 day":
    "Answered calls where Jev gave at least a 50% chance of more than one day — jobs FAV booked as 1 day that Jev thinks will run longer.",
  "Jev = table's top pick":
    "How often Jev's pick matches the most likely answer from past similar jobs. High = Jev mostly agrees with history.",
  "Jobs finished so far": "Calls whose job has finished, so Jev's answer can be checked against what really happened.",
  "Input tokens": "The total text sent to Jev (in tokens), and roughly what it cost at the quoted $0.04 per million.",
  Graded:
    "Answers checked against what really happened (the job finished in FileMaker). Pending = not finished yet. Cancelled / written-off jobs are never graded.",
  "Jev hit rate": "Of the graded answers, how often Jev's pick (1, 2, 3 or 4+ days) matched the real working days.",
  "Overruns caught":
    "An overrun = the job took longer than FAV's 1-day booking. This counts how many overruns were among the 10% of calls Jev was most worried about (its highest chance of more than one day). Higher = Jev spots the long jobs.",
  "Assigned crew = top pick":
    'Of the jobs with a crew ranking, how often the install manager assigned the crew the ranking put first. "among the free crews" = how often the assigned crew was one of the crews free that day at all.',
};

export const CHART_HELP = {
  "Calls over time": "How many Jev calls came in per hour (when there's under 3 days of data) or per day. Hover a bar for the count.",
  "Jev's chance a job takes more than 1 day":
    "How many jobs got each chance from Jev, in 10% steps. Most sit near 0% because most installs take one day; the jobs on the right are the ones Jev expects to run long.",
  "Latency (job read + Jev)": "How long the Jev calls took, grouped by milliseconds.",
};

export const CARD_HELP = {
  "Jev vs the reference table":
    "For each answer (1, 2, 3, 4+ days): Jev's chance (blue) next to past similar jobs company-wide (orange) and at this job's office (gray). Bars close together = Jev agrees with history.",
  "The call": "Technical facts about this one call to Jev.",
  "What Jev was told about the job":
    "The job facts Jev was given — never customer names, addresses or phone numbers. The full text is under \"Request sent to Jev\".",
  "What FAV did": "What FAV actually booked on this call. Nothing Jev says changes it.",
  "The job now": "The job in the Scheduler right now — it may have changed since FAV booked it.",
  Outcome: "How Jev's answer compares with what really happened, once the job has finished in FileMaker.",
  "Crews free that day":
    "The crews with nothing booked on FAV's chosen date, ranked by how likely each is to run past one day on this job (lowest first). Arithmetic from past jobs — no AI, and nothing is booked from it.",
  "Request sent to Jev": "Exactly what was sent to Jev — the question and the job facts.",
  "Jev's response": "Exactly what Jev answered, including how many tokens the call used.",
  "Full log row":
    "The whole saved record for this call, as stored in Mongo — the JevShadowLog collection for live calls, JevReplayLog for replays.",
  Grade: "The saved grade for this call, as stored in the JevAnswerGrades collection.",
};

export const FIELD_HELP = {
  // The call
  "Keller ID": "The job's Keller ID.",
  When: "When FAV asked Jev.",
  Mode: 'all = a live FAV booking · uat = a test call · replay = a past call re-run by PRED. "developerMode" = it used the UAT database.',
  Status: "ok = answered · skipped = not asked · timeout = no answer in 1.5 s · error = failed.",
  Latency: "How long the call took, in milliseconds.",
  Model: "Which version of Jev answered.",
  "Lambda / table": "The version of our booking code, then the version of the past-jobs table it used.",
  Tokens: "How much text went to Jev and came back, in tokens (roughly ¾ of a word each).",
  // What Jev was told
  "Floor type": "The job's floor type.",
  "More than one floor type": "Yes if the invoice mixes materials (for example carpet and vinyl).",
  "Labour $": "The job's labour dollars from the invoice.",
  "Labour band": 'Where this job\'s labour $ falls among past jobs of the same floor type (for example "highest 20%").',
  Office: "The Keller office the job belongs to.",
  "Rush / expedite": "Yes if a rush or expedite flag is set on the job in FileMaker.",
  "Similar past jobs": "How many finished installs the comparison numbers are based on.",
  // What FAV did
  "Reserve requested": "FAV asked to book a date on this call.",
  Reserved: "A date was actually written to the job.",
  Date: "The date FAV booked.",
  "Booked working days": "How many days FAV booked — always 1 today.",
  "Skip reason": "Why FAV didn't book a date (for example, no open slot in the date range).",
  Degraded: "Yes if some data couldn't be read, so FAV played safe and booked nothing.",
  // The job now
  Now: "The job is no longer in the Scheduler's job list.",
  "Status code": "The job's FileMaker status: 4 = scheduled · 8–15 = done · 20 = cancelled · 21 = written off · 30–31 = repair.",
  Scheduled: "The job's scheduled start date now.",
  Completion: "The job's expected completion date now.",
  "Booked working days now": "Working days between start and completion as booked now (Sundays not counted).",
  "Crew assigned": "Yes if an installer crew has been assigned.",
  Finished: "Yes once the job is done, so Jev's answer can be graded.",
  // Outcome
  Graded: "Whether this answer has been checked against what happened.",
  "Job status": "The job's FileMaker status when it was last checked.",
  "Last checked": "When the nightly grading last looked at this job.",
  "Actual working days": "How many working days the install really took (start to completion, Sundays not counted).",
  "Start → completion": "The real start and completion dates from FileMaker.",
  Installed: "The installed date recorded in FileMaker.",
  "FAV booked": "How many days FAV booked for it.",
  "Ran longer than booked": "Yes if the job took more days than FAV booked — an overrun.",
  "Jev's day choice right": "Whether Jev's pick (1, 2, 3 or 4+ days) matched what really happened.",
  "Jev's chance of what happened":
    "The chance Jev gave the answer that turned out to be true. Higher is better — 90% means Jev was confident and right.",
  "Multi-day yes/no": "Jev's chance the job would take more than one day, next to whether it actually did.",
  "Crew lead": "The payroll number of the crew that did the job.",
};

// The crew card's column headers.
export const CREW_COLUMN_HELP = {
  "#": "Rank — 1 = least likely to run past one day.",
  Crew: 'The crew\'s payroll number. "← assigned" marks the crew the install manager actually assigned.',
  "Days (1/2/3/4+)": "That crew's chance of finishing in 1, 2, 3 or 4+ days, as one bar coloured as in the legend under the call table. Hover a segment for its percentage.",
  Suggested: "The fewest days with at least an 80% chance of being enough for this crew.",
  "P(>1 day)": "That crew's chance of taking more than one working day on this job.",
  "Capacity days": "The job's square feet divided by this crew's best daily capacity for the floor type — about how many days of work it is for them.",
  "Shared from": "The crew's home office, when it isn't this job's office (a shared crew). Not a penalty.",
  Flags: "Why a crew sorts lower: no capacity on file for this floor type, no furniture capability, or little job history.",
};

/** Help for a tile label, ignoring a "(Live)" / "(Replays)" suffix. */
export function tileHelp(label) {
  return TILE_HELP[String(label).replace(/ \((Live|Replays)\)$/, "")] || null;
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Explanations for the remaining labels (Leland, 2026-10-08: "I still see confusing
// labels in there without tooltips").
export const CONTROL_HELP = {
  Status: "Show only calls with this result: ok (answered), skipped (not asked), timeout (no answer in time) or error.",
  "Include replays":
    "Also list PRED's replays — past FAV calls re-run through Jev to test it. Replays never booked anything.",
  Live: "The list is live: new FAV calls appear by themselves. It shows the newest calls only.",
  Calls: "Every Jev call, newest first. Click a column title to sort, or a row to see everything about that call.",
  Legend: "The colours used in each Jev probabilities bar: 1, 2, 3 and 4+ working days.",
  Heading:
    "Jev's pick, its confidence and its chance of more than one day. Confidence is Jev's own 0–1 score for its pick — not the same number as the chance of that answer.",
  Jev: "Jev's chance for each answer.",
  "Reference table (no AI)": "The share of similar finished installs company-wide (same floor type, similar labour $) that took each number of days.",
  "This office (no AI)": "The same, using only this job's office.",
};

// FileMaker status names (docs/status-codes.md) for the "s4"-style codes in the table.
const STATUS_NAMES = {
  0: "unknown", 1: "Created", 1.1: "Created (IM Action)", 1.2: "Created (OC/Tech Action)", 1.3: "Created (Other/Admin)",
  1.4: "Created (IM Resolved)", 1.5: "Created (Self Scheduled)", 1.6: "Created (Refigures)", 1.7: "Created (Detail Required)",
  2: "Verified", 2.1: "Verified (Suppressed)", 3: "Ready to Schedule", 3.1: "Ready to Schedule (Suppressed)",
  4: "Scheduled", 4.1: "Forced Re-Schedule",
  5: "Ready to Dispatch", 6: "In Transit", 7: "Checked In", 8: "Checked Out", 9: "COC Received",
  10: "Awaiting Response", 10.1: "Awaiting Response", 10.2: "Awaiting Response (Action Required)",
  10.3: "Awaiting Response (Issue Resolved)", 10.4: "Awaiting Response (1st Call Attempt)",
  10.5: "Awaiting Response (2nd Call Attempt)", 10.6: "Awaiting Response (Call Back Later)",
  10.7: "Awaiting Response (Suppressed)", 11: "Responded", 12: "Unresolved Issue",
  12.1: "Unresolved Issue (Resolution Scheduled)", 12.2: "Unresolved Issue (Action Required)",
  12.3: "Unresolved Issue (Issue Resolved)", 12.4: "Unresolved Issue (1st Call Attempt)",
  12.5: "Unresolved Issue (2nd Call Attempt)", 12.6: "Unresolved Issue (Call Back Later)",
  12.7: "Unresolved Issue (Suppressed)", 13: "Customer Concluded", 14: "Billed", 14.1: "Billed (Suppressed)", 15: "Paid", 20: "Cancelled", 21: "Write Off", 30: "Repair Open",
  30.1: "Repair Open (Suppressed)", 31: "Repair Completed",
};
/** "Created (Self Scheduled)" for 1.5, "Suppressed" for 3.1; an unlisted sub-status
 *  falls back to its whole-number name; null if unknown. */
export function statusName(code) {
  const n = parseFloat(code);
  if (!Number.isFinite(n)) return null;
  return STATUS_NAMES[n] || STATUS_NAMES[Math.floor(n)] || null;
}

const REASONS = {
  unknown_floor_type: "the floor type isn't one Jev knows",
  no_labour_dollars: "the invoice has no labour $",
  job_not_found: "the job wasn't found",
  job_lookup_failed: "the job couldn't be read",
  degraded: "some data couldn't be read, so FAV played safe",
  no_api_key: "Jev's key isn't set",
  bad_response: "Jev's answer couldn't be read",
  fetch_failed: "the network call failed",
  timeout: "Jev didn't answer in time",
  settle_cap: "the whole call ran past its time limit",
};
const reasonText = (reason) => REASONS[reason] || String(reason || "").replace(/_/g, " ");
const pctText = (x) => (x == null || !Number.isFinite(x) ? null : Math.round(100 * x) + "%");
const daysText = (n) => `${n} working day${String(n) === "1" ? "" : "s"}`;

/** The hover text for ONE cell of the call table: what THIS value means, in plain
 *  English. `v` holds the row's already-computed values (JevViewer's CallRow), so the
 *  text always describes exactly what the cell shows. Returns null for no tooltip. */
export function cellTip(label, v) {
  switch (label) {
    case "Time":
      return v.replay
        ? "A replay: a past FAV call re-run through Jev by PRED to test it. It never booked anything."
        : "When FAV asked Jev about this job.";
    case "Keller ID":
      return "Click the row to see everything about this call.";
    case "Status":
      if (v.status === "ok") return "Jev answered in time.";
      if (v.status === "skipped") return `Jev wasn't asked: ${reasonText(v.reason)}.`;
      if (v.status === "timeout") return "Jev didn't answer in time — usually the first call after the function sat idle. The booking was not affected.";
      if (v.status === "error") return `The call failed: ${reasonText(v.reason)}. The booking was not affected.`;
      return null;
    case "Labour $":
      return v.labour == null ? "No labour $ on the invoice." : "The labour $ on this job's invoice — the strongest single clue to how long it takes.";
    case "Jev":
      return v.choice ? `Jev's single best guess: ${daysText(v.choice)}. Hover the bar to see its chance of each answer.` : "No answer from Jev on this call.";
    case "Jev probabilities":
      return v.probabilities
        ? ["1", "2", "3", "4+"].map((b) => `${b} day${b === "1" ? "" : "s"}: ${pctText(v.probabilities[b] || 0)}`).join(" · ")
        : null;
    case "P(>1 day)":
      return pctText(v.pMulti) ? `Jev gives this job a ${pctText(v.pMulti)} chance of taking more than one working day.` : null;
    case "Table P(>1)":
      return pctText(v.refPMulti)
        ? `${pctText(v.refPMulti)} of similar finished installs (same floor type, similar labour $) took more than one working day. No AI — the yardstick for Jev.`
        : null;
    case "FAV booked":
      if (v.fav?.reserved) return `FAV booked ${v.fav.reservedDate} for ${daysText(v.fav.bookedWorkingDays ?? 1)}. Nothing Jev says changes that.`;
      if (v.fav?.reserveSkippedReason) return `FAV didn't book a date: ${v.fav.reserveSkippedReason}.`;
      if (v.replay) return "Replays don't book anything.";
      return "FAV didn't ask to book a date on this call.";
    case "Booking now": {
      if (!v.now) return v.replay ? "Replays aren't linked to the live job list." : "This job isn't in the Scheduler's job list.";
      if (v.now.gone) return "The job is no longer in the Scheduler's job list (finished long ago, cancelled, or removed).";
      const name = statusName(v.now.statusCode);
      const booked = v.now.bookedWorkingDays ? `Booked for ${daysText(v.now.bookedWorkingDays)}` : "Not on the schedule yet";
      return `${booked}. Status ${v.now.statusCode ?? "?"}${name ? ` = ${name}` : ""}.${v.now.finished ? " Finished, so Jev's answer can be graded." : ""}`;
    }
    case "Actual":
      if (!v.grade || v.grade.text === "—") return "Not checked yet — the nightly grading picks it up.";
      if (v.grade.text === "pending") return "The job hasn't finished in FileMaker yet. Checked again nightly.";
      if (v.grade.text === "cancelled") return "Cancelled or written off — never graded.";
      if (v.grade.hit === null) return `Took ${v.grade.text.replace(/d$/, "")} working days (Sundays not counted).`;
      return `Really took ${daysText(v.grade.text.replace(/d$/, ""))} (Sundays not counted) — Jev's pick was ${v.grade.hit ? "right ✓" : "wrong ✗"}.`;
    case "ms":
      return v.latencyMs == null ? null : `This call took ${v.latencyMs} ms: reading the job + asking Jev.`;
    default:
      return null;
  }
}
